import { endOfDay, isAfter } from 'date-fns';
import { Dexie, type EntityTable } from 'dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import { useCallback, useEffect, useMemo } from 'react';

export interface UnsavedChange<TData> {
    key: string;
    changes: TData;
}

export interface UnsavedChangesDatabase extends Dexie {
    // biome-ignore lint/suspicious/noExplicitAny: we need to use any
    unsavedChanges: EntityTable<UnsavedChange<any>, 'key'>;
}

export function createUnsavedChangesDatabase() {
    const db = new Dexie('UnsavedChangesDatabase') as UnsavedChangesDatabase;
    db.version(1).stores({
        unsavedChanges: 'key, changes',
    });
    return db;
}

const db = createUnsavedChangesDatabase();

async function setUnsavedChanges<TData>(key: string, changes: TData) {
    await db.unsavedChanges.put({ key, changes });
}

export async function setUnsavedChangesExpiration(expirationTime = endOfDay(new Date())) {
    await db.unsavedChanges.put({ key: 'unsaved-changes-expiration', changes: expirationTime.toISOString() });
}

export function isUnsavedChangesExpired(currentTime: Date, expirationDate?: string) {
    if (!expirationDate) {
        return true;
    }

    return isAfter(currentTime, new Date(expirationDate));
}

export function getMillisecondsUntilUnsavedChangesExpiration(currentTime: Date, expirationDate?: string) {
    if (!expirationDate) {
        return null;
    }

    return new Date(expirationDate).getTime() - currentTime.getTime();
}

async function clearExpiredUnsavedChanges() {
    await db.unsavedChanges.clear();
}

// implementation
export function useUnsavedChanges<T>(
    key: string,
    defaultValue?: T
): [
    typeof defaultValue extends undefined ? T | undefined : T,
    (arg: T | ((state: T | undefined) => T)) => Promise<void>,
    () => Promise<UnsavedChange<T> | undefined>,
] {
    const clearIfExpired = useCallback(async () => {
        // Re-check expiration against IndexedDB before mutating state to prevent
        // stale unsaved data from surviving long-lived tabs.
        const currentExpirationDate = await db.unsavedChanges.where({ key: 'unsaved-changes-expiration' }).first();

        if (!currentExpirationDate?.changes) {
            return;
        }

        if (!isUnsavedChangesExpired(new Date(), currentExpirationDate.changes)) {
            return;
        }

        await clearExpiredUnsavedChanges();
    }, [key]);

    const getExpirationDate = useCallback(async () => {
        return db.unsavedChanges.where({ key: 'unsaved-changes-expiration' }).first();
    }, []);

    const expirationDate = useLiveQuery(() => {
        return getExpirationDate();
    }, [getExpirationDate]);

    useEffect(() => {
        if (!expirationDate?.changes) {
            return;
        }

        const millisecondsUntilExpiration = getMillisecondsUntilUnsavedChangesExpiration(
            new Date(),
            expirationDate.changes
        );

        if (millisecondsUntilExpiration === null || millisecondsUntilExpiration <= 0) {
            void clearIfExpired();
            return;
        }

        // Schedule a one-shot cleanup so unsaved state expires even without user interaction.
        const timeoutId = window.setTimeout(() => {
            void clearIfExpired();
        }, millisecondsUntilExpiration + 1);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [expirationDate?.changes, key, clearIfExpired]);

    useEffect(() => {
        // Tabs can stay open for days; revalidate when the tab becomes active again.
        const handleFocus = () => {
            void clearIfExpired();
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                void clearIfExpired();
            }
        };

        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [key, clearIfExpired]);

    const isExpired = useMemo(() => {
        const now = new Date();
        return isUnsavedChangesExpired(now, expirationDate?.changes);
    }, [expirationDate?.changes]);

    useEffect(() => {
        if (!expirationDate || !isExpired) {
            return;
        }

        // Keep the db clean once expiration is observed by the reactive query.
        void clearExpiredUnsavedChanges();
    }, [expirationDate, isExpired]);

    const getUnsavedChanges = useCallback(async () => {
        return isExpired ? undefined : db.unsavedChanges.where({ key }).first();
    }, [key, isExpired]);

    const unsavedChanges = useLiveQuery(() => {
        if (key) {
            return getUnsavedChanges();
        }
    }, [getUnsavedChanges]);

    const setValue = useCallback(
        async (arg: T | ((state: T | undefined) => T)): Promise<void> => {
            let value: T;
            if (typeof arg === 'function') {
                const updater = arg as (state: T | undefined) => T;
                value = updater(unsavedChanges?.changes);
            } else {
                value = arg;
            }
            await setUnsavedChangesExpiration();
            return setUnsavedChanges(key, value);
        },
        [key, unsavedChanges]
    );

    return [unsavedChanges?.changes ?? defaultValue, setValue, getUnsavedChanges];
}
